"use client";

import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import Image from "next/image";
import { useRef, useCallback, useState } from "react";
import clsx from "clsx";
import { OurProductsSectionInServicePageQueryResult, OurServicesSectionInServicePageQueryResult } from "@/sanity.types";
import { urlFor } from "@/sanity/lib/image";
import BadgeRoundedSquare from "../../../utils/badge-rounded-square/BadgeRoundedSquare";
import footerArrow from "@assets/images/footer-arrow.svg";

type ServicePageSlider = {
	sliderItems:
		| NonNullable<OurProductsSectionInServicePageQueryResult>["items"]
		| NonNullable<OurServicesSectionInServicePageQueryResult>["items"];
};

export default function ServicePageSlider({ sliderItems }: ServicePageSlider) {
	const [emblaRef, emblaApi] = useEmblaCarousel(
		{
			loop: false,
			align: "start",
			containScroll: "trimSnaps",
			skipSnaps: false,
			slidesToScroll: 1,
		},
		[Autoplay({ delay: 8000, stopOnInteraction: false })],
	);
	const viewportRef = useRef<HTMLDivElement | null>(null);
	const [activeSlideIndex, setActiveSlideIndex] = useState<number | null>(null);

	const handleSlideClick = (index: number) => {
		setActiveSlideIndex(activeSlideIndex === index ? null : index);
	};

	const scrollPrev = useCallback(() => {
		if (emblaApi) emblaApi.scrollPrev();
	}, [emblaApi]);

	const scrollNext = useCallback(() => {
		if (emblaApi) emblaApi.scrollNext();
	}, [emblaApi]);

	return (
		<div className="embla group relative">
			<div className="absolute -top-20 left-0 z-30 mb-4 hidden gap-2 sm:ml-5 sm:flex">
				<button onClick={scrollPrev} className="cursor-pointer" aria-label="Previous slide">
					<div className="relative size-13">
						<Image
							src={footerArrow}
							fill
							sizes="100%"
							alt="footer arrow"
							className="rotate-180 object-contain opacity-25 transition-opacity duration-300 hover:opacity-100"
						/>
					</div>
				</button>

				<button onClick={scrollNext} className="cursor-pointer" aria-label="Next slide">
					<div className="relative size-13">
						<Image
							src={footerArrow}
							fill
							sizes="100%"
							alt="footer arrow"
							className="object-contain opacity-25 transition-opacity duration-300 hover:opacity-100"
						/>
					</div>
				</button>
			</div>

			<div
				className="embla__viewport overflow-hidden"
				ref={(el) => {
					emblaRef(el);
					viewportRef.current = el;
				}}
			>
				<div className="embla__container mx-3.5 flex sm:mx-5">
					{sliderItems.map((item, index) => {
						const isActive = activeSlideIndex === index;
						return (
							<div
								key={item._key || index}
								className="embla__slide group/slide relative mr-[0.438rem] w-[77vw] min-w-[18.75rem] flex-shrink-0 cursor-pointer last:mr-0 sm:mr-2.5 sm:w-[calc(30%-0.875rem)]"
								style={{
									flex: "0 0 auto",
								}}
								onClick={() => handleSlideClick(index)}
							>
								{item.image?.asset && (
									<div className="relative aspect-square min-h-[18.75rem] overflow-hidden rounded-lg">
										<div
											className={clsx(
												"bg-navy-blue-deep absolute inset-0 z-10 transition-opacity duration-500 group-hover/slide:opacity-100",
												{
													"opacity-100": isActive,
													"opacity-0": !isActive,
												},
											)}
										/>

										<BadgeRoundedSquare
											text={String(index + 1).padStart(2, "0")}
											backgroundColor="bg-olive-green-light"
											textColor="text-graphite-black"
											position="absolute"
											top="1.25rem"
											left="1.25rem"
											className="z-20"
										/>

										<Image
											src={urlFor(item.image.asset).url()}
											alt={item.image.alt || `Slide ${index + 1}`}
											width={406}
											height={406}
											className={clsx(
												"h-full w-full object-cover transition-opacity duration-500 group-hover/slide:opacity-0",
												{
													"opacity-0": isActive,
												},
											)}
										/>
										<div className="bg-graphite-black/20 absolute inset-0" />

										<div className="pointer-events-none absolute right-0 bottom-0 left-0 z-20 flex flex-col items-start p-5 pb-7.5 transition-transform duration-500 ease-in-out sm:pb-7.5">
											<h3 className="text-h7-mob sm:text-h5 mb-0 text-white transition-all duration-500">
												{item.title}
											</h3>
											<p
												className={clsx(
													"text-body-m text-white transition-all duration-500",
													"invisible max-h-0 opacity-0",
													"group-hover/slide:visible group-hover/slide:mt-7.5 group-hover/slide:max-h-[18.75rem] group-hover/slide:opacity-100",
													{
														"visible mt-7.5 max-h-[18.75rem] opacity-100": isActive,
													},
												)}
											>
												{item.description}
											</p>
										</div>
									</div>
								)}
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
}
